import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import get from "lodash/get";
import { useDispatch } from "store";
import { runSampleProximitySensors } from "store/reducers";
import { TAsset } from "types";
import { TProximitySensorReport } from "types/features";
import {
  applyPoll,
  buildSensorLinkMap,
  deriveSensorHealthView,
  emptySession,
  POLL_INTERVAL_MS,
  SensorSession,
} from "./sensorHealth.utility";

export type UseSensorHealthOptions = {
  intervalMs?: number;
  sampler?: () => Promise<TProximitySensorReport[]>;
};

export const useSensorHealthCheck = (
  storageId: string,
  assets: TAsset[],
  options: UseSensorHealthOptions = {},
) => {
  const dispatch = useDispatch();
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;

  const [running, setRunning] = useState(false);
  const [session, setSession] = useState<SensorSession>(emptySession);
  const [error, setError] = useState<string | null>(null);

  const runningRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const linkMap = useMemo(() => buildSensorLinkMap(assets), [assets]);
  const view = useMemo(() => deriveSensorHealthView(session, linkMap), [session, linkMap]);

  const defaultSampler = useCallback(
    () => dispatch(runSampleProximitySensors({ storageId })).unwrap(),
    [dispatch, storageId],
  );
  const sampler = options.sampler ?? defaultSampler;

  const tick = useCallback(async () => {
    if (!runningRef.current) return;
    try {
      const reports = await sampler();
      setSession((prev) => applyPoll(prev, reports));
      setError(null);
    } catch (err) {
      // Keep polling through a transient device blip; surface the latest error. Prefer the API's
      // message: axios's own ("Request failed with status code 503") doesn't say what went wrong.
      setError(get(err, "response.data.message") || (err instanceof Error ? err.message : "Failed to sample sensors"));
    } finally {
      if (runningRef.current) {
        timerRef.current = setTimeout(tick, intervalMs);
      }
    }
  }, [sampler, intervalMs]);

  const start = useCallback(() => {
    if (runningRef.current) return;
    setSession(emptySession());
    setError(null);
    runningRef.current = true;
    setRunning(true);
    void tick();
  }, [tick]);

  const stop = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      runningRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { running, view, error, start, stop, pollCount: session.pollCount };
};
